/**
 * Page publique d'une fiche d'arrêt — nouveau design
 * URL : /jurisprudence/:jur/:chambre/:date/:numero
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Loader } from '@/components/Loader';
import {
  PublicHeader, PublicFooter, MCPBadge,
  ProcedureTimeline,
  type PublicFiche, type Relations,
} from '@/components/jurisprudence';
import { FreeBanner, PaywallOverlay, usePaywall } from '@/components/jurisprudence/paywall';
import { LegalGraphTab }     from '@/components/jurisprudence/LegalGraphTab';
import { FicheHeader }       from '@/components/jurisprudence/FicheHeader';
import { FicheTabRelations } from '@/components/jurisprudence/FicheTabRelations';
import { FicheTabSource }    from '@/components/jurisprudence/FicheTabSource';
import { GraphStatusBanner } from '@/components/jurisprudence/GraphStatusBanner';
import { FicheAgentChat }    from '@/components/jurisprudence/FicheAgentChat';
import { LocalAgentChat }    from '@/components/jurisprudence/LocalAgentChat';
import { desktopBridge }     from '@/AppMode';
import { usePublicAuth }     from '@/hooks/usePublicAuth';
import { useVisitorId }      from '@/hooks/useVisitorId';
import { useGraphCheck }     from '@/hooks/useGraphCheck';
import { articlePathFromFiche, articleLabel, slugifyCode } from '@/utils/articleSlug';

// ── Mapping domaine → agent ───────────────────────────────────────────────────
const DOMAINE_AGENT_NAMES: Record<string, { name: string; emoji: string }> = {
  'droit social':              { name: 'Laborius',     emoji: '👷' },
  'droit civil':               { name: 'Baudelinius',  emoji: '⚖️' },
  'droit commercial':          { name: 'Mercatorius',  emoji: '🏢' },
  'droit pénal':               { name: 'Carrius',      emoji: '🚔' },
  'droit fiscal':              { name: 'Fiscus',       emoji: '💰' },
  'droit administratif':       { name: 'Cesedus',      emoji: '🏛️' },
  'procédure civile':          { name: 'Baudelinius',  emoji: '⚖️' },
  'droit immobilier':          { name: 'Haussmannus',  emoji: '🏗️' },
  "droit de l'environnement":  { name: 'Vidar',        emoji: '🌿' },
  "droit de la construction":  { name: 'Haussmannus',  emoji: '🏗️' },
  'droit pénal routier':       { name: 'Malus',        emoji: '🚗' },
};

// ── Suggestions agent (clés i18n, traduites au rendu) ─────────────────────────
const AGENT_SUGGESTION_KEYS = [
  'fiche.page.suggestions.s1',
  'fiche.page.suggestions.s2',
  'fiche.page.suggestions.s3',
  'fiche.page.suggestions.s4',
];

// ── Types ─────────────────────────────────────────────────────────────────────
type DeepTab = 'faits' | 'relations' | 'graphe' | 'source';

// ── JSON-LD ───────────────────────────────────────────────────────────────────
function buildJsonLd(fiche: PublicFiche, url: string): string {
  return JSON.stringify({
    '@context':  'https://schema.org',
    '@type':     'LegalCase',
    name:        `${fiche.jurisdiction} — ${fiche.chamber} — ${fiche.decision_date} — ${fiche.number}`,
    identifier:  fiche.ecli,
    dateCreated: fiche.decision_date,
    description: fiche.probleme?.slice(0, 300),
    keywords:    (fiche.mots_cles || []).join(', '),
    about:       (fiche.concepts || []).map(c => ({ '@type': 'Thing', name: c })),
    publisher:   { '@type': 'Organization', name: 'SmartLawyer', url: 'https://smartlawyer.ai' },
    url,
  });
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function PublicFichePage() {
  const { t } = useTranslation();
  const { jur, ville, chambre, date, numero } = useParams<{
    jur: string; ville?: string; chambre: string; date: string; numero: string;
  }>();

  const slugPath = ville
    ? `${jur}/${ville}/${chambre}/${date}/${numero}`
    : `${jur}/${chambre}/${date}/${numero}`;

  // ── State ──────────────────────────────────────────────────────────────────
  const [fiche, setFiche]                     = useState<PublicFiche | null>(null);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState<string | null>(null);
  const [deepTab, setDeepTab]                 = useState<DeepTab>('faits');
  const [agentName, setAgentName]             = useState<string | null>(null);
  const [agentEmoji, setAgentEmoji]           = useState<string>('⚖️');
  const [agentImage, setAgentImage]           = useState<string | null>(null);
  const [relations, setRelations]             = useState<Relations | null>(null);
  const [loadingRel, setLoadingRel]           = useState(false);
  const [source, setSource]                   = useState<{ text: string; legifrance_url: string | null } | null>(null);
  const [loadingSource, setLoadingSource]     = useState(false);
  const [sourceError, setSourceError]         = useState(false);
  const [chatOpen, setChatOpen]               = useState(false);
  // App desktop : agent exécuté en local (Ollama) au lieu du serveur
  const [localAgent, setLocalAgent]           = useState(false);
  const hasDesktopBridge                      = Boolean(desktopBridge());
  const [initialPrompt, setInitialPrompt]     = useState<string | undefined>(undefined);

  const { user, getToken }                               = usePublicAuth();
  const visitorId                                        = useVisitorId();
  const { blocked, remaining, userType, applyServerState } = usePaywall();

  const relationsKey             = useRef(0);
  const relationsForFicheId      = useRef<string | null>(null);
  const fetchingRelations        = useRef(false);

  // ── Graph check ────────────────────────────────────────────────────────────
  const handleRebuildDone = useCallback(() => {
    relationsForFicheId.current = null;
    fetchingRelations.current   = false;
    relationsKey.current        += 1;
    setRelations(null);
  }, []);

  const { status: graphStatus, report: graphReport, isAdmin, triggerRebuild, rebuildError } =
    useGraphCheck({
      ficheId:       fiche?.id ?? null,
      getToken,
      userEmail:     user?.email ?? null,
      onRebuildDone: handleRebuildDone,
    });

  // ── Fetch fiche ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!jur || !chambre || !date || !numero) return;

    setRelations(null);
    setSource(null);
    setSourceError(false);
    setDeepTab('faits');
    setLoading(true);
    setError(null);
    setChatOpen(false);
    fetchingRelations.current   = false;
    relationsForFicheId.current = null;

    const doFetch = async () => {
      try {
        // Headers : Firebase token (utilisateurs connectés) + FingerprintJS (anonymes)
        const headers: Record<string, string> = {
          'X-Visitor-Id': visitorId,
        };
        try {
          const token = await getToken();
          if (token) headers['Authorization'] = `Bearer ${token}`;
        } catch { /* anonyme */ }

        const r = await fetch(`/api/fiches/by-slug/${slugPath}`, {
          headers,
          credentials: 'include',
        });
        if (r.status === 404) throw new Error('fiche.page.error_not_found');
        if (!r.ok)            throw new Error('fiche.page.error_server');

        const data = await r.json() as PublicFiche & { _paywall?: { blocked: boolean; remaining: number; user_type: string } };

        data.concepts              = Array.isArray(data.concepts)              ? data.concepts              : [];
        data.mots_cles             = Array.isArray(data.mots_cles)             ? data.mots_cles             : [];
        data.articles              = Array.isArray(data.articles)              ? data.articles              : [];
        data.themes                = Array.isArray(data.themes)                ? data.themes                : [];
        data.cite_arrets           = Array.isArray(data.cite_arrets)           ? data.cite_arrets           : [];
        data.procedure_graph       = Array.isArray(data.procedure_graph)       ? data.procedure_graph       : [];
        data.arrets_impactes       = Array.isArray(data.arrets_impactes)       ? data.arrets_impactes       : [];
        data.cite_arrets_qualifies = Array.isArray(data.cite_arrets_qualifies) ? data.cite_arrets_qualifies : [];

        setFiche(data);

        // Le backend a déjà enregistré la vue et retourne le statut paywall
        if (data._paywall) applyServerState(data._paywall);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    doFetch();
  }, [jur, ville, chambre, date, numero]);

  // ── Log consultation fiche ─────────────────────────────────────────────────
  useEffect(() => {
    if (!fiche?.ecli && !fiche?.number) return;
    const body = {
      ecli:          fiche.ecli          || null,
      number:        fiche.number        || null,
      chamber:       fiche.chamber       || null,
      jurisdiction:  fiche.jurisdiction  || null,
      decision_date: fiche.decision_date || null,
      referrer:      document.referrer   || null,
      uid:           user?.uid           || null,
      email:         user?.email         || null,
    };
    fetch('/api/fiches/log-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => {});
  }, [fiche?.ecli]);

  // ── Fetch agent info ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!fiche?.domaine) return;
    fetch(`/api/fiches-agent/${fiche.id}/agent-info`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.name)       setAgentName(data.name);
        if (data?.icon_emoji) setAgentEmoji(data.icon_emoji);
        if (data?.image)      setAgentImage(data.image);
      })
      .catch(() => {});
  }, [fiche?.id, fiche?.domaine]);

  // ── Fetch relations ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!fiche?.id) return;
    if (fetchingRelations.current) return;
    if (relationsForFicheId.current === fiche.id) return;

    fetchingRelations.current = true;
    setLoadingRel(true);

    const procedure_graph     = fiche.procedure_graph  ?? [];
    const cite_arrets         = fiche.cite_arrets      ?? [];
    const cited_by_ids        = fiche.cited_by_ids     ?? [];
    const resolved_target_ids = procedure_graph
      .filter(l => l.resolved && l.target_id)
      .map(l => l.target_id!);

    fetch(`/api/fiches/${fiche.id}/relations`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ cite_arrets, cited_by_ids, resolved_target_ids }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const { cited_fiches, targets_by_id, cited_by_fiches } = data;

        const byRef: Record<string, typeof cited_fiches[0]> = {};
        for (const f of cited_fiches) {
          if (f.ecli)   byRef[f.ecli]   = f;
          if (f.number) byRef[f.number] = f;
        }
        const cite_arrets_qualifies_enriched = (fiche.cite_arrets_qualifies ?? []).map(c => {
          const found = byRef[c.ref];
          return { ...c, found: !!found, fiche_id: found?.id ?? null, slug: found?.slug ?? null,
            decision_date: found?.decision_date ?? null, chamber: found?.chamber ?? null,
            solution: found?.solution ?? null, probleme: found?.probleme ?? null,
            citation_count: found?.citation_count ?? 0 };
        });

        const proc_graph_resolved = procedure_graph.map(lnk => {
          const tp = lnk.target_id ? (targets_by_id[lnk.target_id] ?? {}) : {};
          return { ...lnk,
            target_ecli:          tp.ecli          ?? '',
            target_number:        tp.number        ?? '',
            target_decision_date: tp.decision_date ?? '',
            target_chamber:       tp.chamber       ?? '',
            target_solution:      tp.solution      ?? '',
            target_jurisdiction:  tp.jurisdiction  ?? '',
            target_probleme:      tp.probleme ? tp.probleme.slice(0, 200) : '',
            target_domaine:       tp.domaine       ?? '',
            target_slug:          tp.slug          ?? '',
          };
        });

        relationsForFicheId.current = fiche.id;

        setRelations({
          fiche_id:          fiche.id,
          cited_fiches, cited_by_fiches,
          cite_arrets,
          cite_arrets_qualifies: cite_arrets_qualifies_enriched,
          procedure_graph:       proc_graph_resolved,
          cited_by_count:        fiche.cited_by_count    ?? 0,
          cited_by_types:        fiche.cited_by_types    ?? {},
          importance_score:      fiche.importance_score  ?? 0,
          graph_built_at:        fiche.graph_built_at    ?? null,
          arrets_impactes:       fiche.arrets_impactes   ?? [],
          procedure_lineage:     fiche.procedure_lineage ?? {},
          articles:              fiche.articles          ?? [],
          concepts:              fiche.concepts          ?? [],
          mots_cles:             fiche.mots_cles         ?? [],
          domaine:               fiche.domaine           ?? '',
          sous_domaine:          fiche.sous_domaine      ?? '',
          citation_count:        fiche.citation_count    ?? 0,
        });
      })
      .catch(console.error)
      .finally(() => {
        setLoadingRel(false);
        fetchingRelations.current = false;
      });
  }, [fiche?.id]);

  // ── Fetch source (lazy) ────────────────────────────────────────────────────
  useEffect(() => {
    if (deepTab !== 'source' || source || !fiche?.id) return;
    setLoadingSource(true); setSourceError(false);
    const params = new URLSearchParams();
    if (fiche.judilibre_id) params.set('judilibre_id', fiche.judilibre_id);
    if (fiche.ecli)         params.set('ecli',         fiche.ecli);
    if (fiche.number)       params.set('number',       fiche.number);
    fetch(`/api/fiches/${fiche.id}/source?${params}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setSource(data))
      .catch(() => setSourceError(true))
      .finally(() => setLoadingSource(false));
  }, [deepTab, fiche?.id]);

  // ── Keyboard: fermer overlay ───────────────────────────────────────────────
  useEffect(() => {
    if (!chatOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setChatOpen(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [chatOpen]);

  // ── Loading / error ────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex justify-center items-center min-h-screen">
      <Loader className="!size-8" />
    </div>
  );

  if (error || !fiche) return (
    <div className="flex flex-col items-center justify-center min-h-screen text-center p-8">
      <p className="text-6xl mb-6">⚖️</p>
      <h1 className="text-2xl font-bold mb-2">{t('fiche.page.not_found_title')}</h1>
      <p className="text-muted-foreground mb-8">{error ? (error.startsWith('fiche.') ? t(error) : error) : t('fiche.page.not_found_desc')}</p>
      <Link to="/jurisprudence"
        className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium">
        {t('fiche.page.back_to_search')}
      </Link>
    </div>
  );

  // ── Computed values ────────────────────────────────────────────────────────
  const canonical     = `https://smartlawyer.ai/jurisprudence/${slugPath}`;
  const pageTitle     = `${fiche.jurisdiction} — ${fiche.chamber}, ${fiche.decision_date} — ${fiche.number} | SmartLawyer`;
  const pageDesc      = fiche.probleme?.slice(0, 160) || t('fiche.page.meta_desc_fallback', { ecli: fiche.ecli });
  const agentSuggestions = AGENT_SUGGESTION_KEYS.map(k => t(k));
  const totalRel      = (fiche.cite_arrets_qualifies?.length ?? fiche.cite_arrets?.length ?? 0);

  const agentDefault      = fiche.domaine
    ? DOMAINE_AGENT_NAMES[fiche.domaine.toLowerCase()] ?? { name: 'Baudelinius', emoji: '⚖️' }
    : { name: 'Baudelinius', emoji: '⚖️' };
  const agentDisplayName  = agentName  || agentDefault.name;
  const agentDisplayEmoji = agentEmoji !== '⚖️' ? agentEmoji : agentDefault.emoji;

  const ficheShortTitle = [
    fiche.jurisdiction,
    fiche.chamber,
    fiche.decision_date?.slice(0, 10),
  ].filter(Boolean).join(' · ');

  // ── Ouvrir le chat avec un prompt optionnel ────────────────────────────────
  function openChat(prompt?: string) {
    setInitialPrompt(prompt);
    setChatOpen(true);
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description"        content={pageDesc} />
        <meta name="keywords"           content={fiche.mots_cles.join(', ')} />
        <link rel="canonical"           href={canonical} />
        <meta property="og:type"        content="article" />
        <meta property="og:title"       content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url"         content={canonical} />
        <meta name="robots"             content="index, follow" />
        <script type="application/ld+json">{buildJsonLd(fiche, canonical)}</script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <PublicHeader />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex gap-8 items-start">

            {/* ════════════════════════════════════════════════════════════════
                Colonne principale
            ════════════════════════════════════════════════════════════════ */}
            <main className="flex-1 min-w-0">

              {/* En-tête existant */}
              <FicheHeader
                fiche={fiche}
                jur={jur!}
                chambre={chambre!}
                date={date!}
                numero={numero!}
                blocked={blocked}
                remaining={remaining}
              />

              <GraphStatusBanner
                status={graphStatus}
                report={graphReport}
                isAdmin={isAdmin}
                triggerRebuild={triggerRebuild}
                rebuildError={rebuildError}
              />

              {!blocked && <FreeBanner remaining={remaining} userType={userType} />}

              {/* ── 0. Analyse RGPD (en premier, seulement si enrichissement LLM présent) ── */}
              {fiche.is_rgpd && (fiche.rgpd_resume || fiche.resume_rgpd || fiche.rgpd_regle_juridique || fiche.rgpd_sous_theme || fiche.rgpd_juridiction_type || fiche.rgpd_base_legale || fiche.mentionne_cnil || (fiche.rgpd_cjue_links ?? []).some(l => l.validated) || (fiche.rgpd_citations_cjue ?? []).length > 0) && (
                <section className="mb-12" id="rgpd">
                  <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-5 flex items-center gap-2">
                    <span className="h-px flex-1 bg-border" />
                    <span className="inline-flex items-center gap-1.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#2563EB" aria-hidden="true">
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
                      </svg>
                      {t('fiche.rgpd.title')}
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </h2>

                  {/* Ligne de contexte : tous les badges d'un coup d'œil */}
                  {(() => {
                    // Libellé traduit d'une valeur enum RGPD (repli : valeur brute lisible)
                    const enumLabel = (ns: string, v: string) =>
                      t(`fiche.rgpd.${ns}.${v}`, { defaultValue: v.replace(/_/g, ' ') });

                    // Label de la personne concernée selon le responsable de traitement
                    const PERSONNE_KEY: Record<string, string> = {
                      employeur:       'employeur',
                      administration:  'administration',
                      plateforme:      'plateforme',
                    };
                    const personneLabel = t(`fiche.rgpd.person.${
                      (fiche.rgpd_responsable_type && PERSONNE_KEY[fiche.rgpd_responsable_type]) || 'default'
                    }`);

                    // Alias des types de juridiction → clé fiche.rgpd.jur.*
                    const JURIDICTION_ALIAS: Record<string, string> = {
                      cour_appel: 'ca', cour_cassation: 'cc',
                    };

                    const ISSUE_MAP: Record<string, { label: string; cls: string; icon: string }> = {
                      gagne:      { label: t('fiche.rgpd.issue.gagne', { person: personneLabel }), cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-300', icon: '✓' },
                      perd:       { label: t('fiche.rgpd.issue.perd', { person: personneLabel }),  cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-300',           icon: '✗' },
                      partiel:    { label: t('fiche.rgpd.issue.partiel'),    cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300 border-orange-300', icon: '◑' },
                      non_statue: { label: t('fiche.rgpd.issue.non_statue'), cls: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400 border-zinc-300',          icon: '—' },
                    };
                    const issueCfg = fiche.rgpd_issue_personne ? ISSUE_MAP[fiche.rgpd_issue_personne] : null;
                    return (
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    {issueCfg && (
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full border ${issueCfg.cls}`}>
                        {issueCfg.icon} {issueCfg.label}
                      </span>
                    )}
                    {fiche.rgpd_sous_theme && (
                      <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 font-medium">
                        🛡️ {enumLabel('sous_theme', fiche.rgpd_sous_theme)}
                      </span>
                    )}
                    {fiche.rgpd_juridiction_type && (
                      <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200">
                        <span className="opacity-60 font-normal">{t('fiche.rgpd.label_jurisdiction')}</span>
                        {enumLabel('jur', JURIDICTION_ALIAS[fiche.rgpd_juridiction_type] ?? fiche.rgpd_juridiction_type)}
                      </span>
                    )}
                    {fiche.rgpd_base_legale && (
                      <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 border border-violet-200 font-medium">
                        <span className="opacity-60 font-normal">{t('fiche.rgpd.label_legal_basis')}</span>
                        {enumLabel('base', fiche.rgpd_base_legale)}
                      </span>
                    )}
                    {fiche.rgpd_responsable_type && (
                      <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border border-slate-200">
                        <span className="opacity-60 font-normal">{t('fiche.rgpd.label_controller')}</span>
                        {enumLabel('resp', fiche.rgpd_responsable_type)}
                      </span>
                    )}
                    {fiche.rgpd_type_donnees && (
                      <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 border border-cyan-200">
                        <span className="opacity-60 font-normal">{t('fiche.rgpd.label_data')}</span>
                        {enumLabel('data', fiche.rgpd_type_donnees)}
                      </span>
                    )}
                  </div>
                    );
                  })()}

                  {/* Règle juridique — le principe dégagé, en avant */}
                  {fiche.rgpd_regle_juridique && (
                    <div className="mb-5 rounded-2xl bg-foreground text-background p-6 shadow-lg">
                      <p className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-3">{t('fiche.rgpd.principle')}</p>
                      <p className="text-base font-medium leading-relaxed italic">« {fiche.rgpd_regle_juridique} »</p>
                    </div>
                  )}

                  {/* Résumé focalisé RGPD — généré par l'agent IA (resume_rgpd) ou annotation manuelle (rgpd_resume) */}
                  {(fiche.resume_rgpd || fiche.rgpd_resume) && (
                    <div className="mb-5 p-5 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/40">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-2">{t('fiche.rgpd.summary')}</p>
                      <p className="text-sm text-foreground leading-relaxed">{fiche.resume_rgpd || fiche.rgpd_resume}</p>
                    </div>
                  )}

                  {/* Implication CNIL */}
                  {fiche.mentionne_cnil && fiche.implication_cnil && (
                    <div className="mb-5 p-5 rounded-xl bg-violet-50 dark:bg-violet-950/20 border border-violet-100 dark:border-violet-900/40">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-violet-600 dark:text-violet-400 mb-2 flex items-center gap-1.5">
                        <span>🏛️</span> {t('fiche.rgpd.cnil_role')}
                      </p>
                      <p className="text-sm text-foreground leading-relaxed">{fiche.implication_cnil}</p>
                    </div>
                  )}

                  {/* Articles RGPD & LIL cités */}
                  {((fiche.rgpd_articles_rgpd ?? []).length > 0 || (fiche.rgpd_articles_lil ?? []).length > 0) && (
                    <div className="mb-5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('fiche.rgpd.articles_cited')}</p>
                      <div className="flex flex-wrap gap-2">
                        {(fiche.rgpd_articles_rgpd ?? []).map((art, i) => (
                          <Link key={`rgpd-${i}`} target="_blank" rel="noopener noreferrer"
                            to={articlePathFromFiche({ code: 'RGPD', article: art })!}
                            className="text-xs px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800/40 font-mono transition-colors">
                            {t('fiche.rgpd.gdpr_art', { art })}
                          </Link>
                        ))}
                        {(fiche.rgpd_articles_lil ?? []).map((art, i) => (
                          <Link key={`lil-${i}`} target="_blank" rel="noopener noreferrer"
                            to={articlePathFromFiche({
                              code: 'Loi informatique et libertés',
                              article: art,
                              version_date: fiche.articles.find(a =>
                                slugifyCode(a.code) === 'loi-informatique-et-libertes' && a.article === art
                              )?.version_date,
                            })!}
                            className="text-xs px-2.5 py-1 rounded-lg bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 font-mono transition-colors">
                            {t('fiche.rgpd.lil_art', { art })}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Délibérations CNIL validées */}
                  {(fiche.rgpd_cnil_validees ?? []).length > 0 && (
                    <div className="mb-5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('fiche.rgpd.cnil_deliberations')}</p>
                      <div className="flex flex-wrap gap-2">
                        {fiche.rgpd_cnil_validees!.map((d, i) => (
                          <Link
                            key={i}
                            to={`/cnil/deliberations/${encodeURIComponent(d)}`}
                            className="text-xs px-2.5 py-1 rounded-lg bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 font-mono hover:bg-violet-200 dark:hover:bg-violet-800/40 transition-colors underline underline-offset-2"
                          >
                            {d} ↗
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mots-clés RGPD */}
                  {(fiche.rgpd_mots_cles ?? []).length > 0 && (
                    <div className="mb-5">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('fiche.rgpd.keywords')}</p>
                      <div className="flex flex-wrap gap-2">
                        {fiche.rgpd_mots_cles!.map((kw, i) => (
                          <span key={i} className="text-xs px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Jurisprudence CJUE fondatrice — liens structurés validés */}
                  {(fiche.rgpd_cjue_links ?? []).filter(l => l.validated).length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">{t('fiche.rgpd.cjue_founding')}</p>
                      <div className="flex flex-col gap-3">
                        {fiche.rgpd_cjue_links!.filter(l => l.validated).map((link, i) => {
                          const isInternal = !!link.point_id;
                          const cardCls = "flex gap-3 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:border-indigo-200 dark:hover:border-indigo-700 transition-colors cursor-pointer";
                          const inner = (
                            <>
                              <span className="text-lg shrink-0">🇪🇺</span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold font-mono text-indigo-700 dark:text-indigo-300">
                                  {link.citation}
                                </p>
                                {link.explication && (
                                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{link.explication}</p>
                                )}
                              </div>
                              {link.confidence && (
                                <span className={`shrink-0 self-start text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                  link.confidence === 'high'   ? 'bg-green-100 text-green-600'  :
                                  link.confidence === 'medium' ? 'bg-amber-100 text-amber-600'  :
                                                                 'bg-zinc-100 text-zinc-500'
                                }`}>
                                  {link.confidence}
                                </span>
                              )}
                            </>
                          );
                          return isInternal ? (
                            <Link key={i} to={`/jurisprudence/cjue/${link.point_id}`} className={cardCls}>
                              {inner}
                            </Link>
                          ) : (
                            <a
                              key={i}
                              href={link.celex
                                ? `https://eur-lex.europa.eu/legal-content/FR/TXT/?uri=CELEX:${link.celex}`
                                : `https://curia.europa.eu/juris/liste.jsf?num=${encodeURIComponent(link.citation)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={cardCls}
                            >
                              {inner}
                              <span className="shrink-0 self-start text-[10px] text-indigo-400 dark:text-indigo-500">↗ EUR-Lex</span>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Jurisprudence CJUE — citations brutes (fallback si pas de liens structurés) */}
                  {(fiche.rgpd_cjue_links ?? []).filter(l => l.validated).length === 0 && (fiche.rgpd_citations_cjue ?? []).length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">{t('fiche.rgpd.cjue_founding')}</p>
                      <div className="flex flex-col gap-2">
                        {fiche.rgpd_citations_cjue!.map((citation, i) => {
                          // Liens non encore validés : si l'arrêt est résolu dans cjue_arrets, lien vers notre fiche CJUE
                          const link = (fiche.rgpd_cjue_links ?? []).find(l => l.citation === citation);
                          const cjueId = link?.celex || link?.point_id;
                          const baseCls = "flex gap-3 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40";
                          const inner = (
                            <>
                              <span className="text-lg shrink-0">🇪🇺</span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold font-mono text-indigo-700 dark:text-indigo-300">{citation}</p>
                                {link?.ecli && <p className="text-xs font-mono text-muted-foreground mt-0.5">{link.ecli}</p>}
                              </div>
                            </>
                          );
                          return cjueId ? (
                            <Link
                              key={i}
                              to={`/jurisprudence/cjue/${encodeURIComponent(cjueId)}`}
                              className={`${baseCls} hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:border-indigo-200 dark:hover:border-indigo-700 transition-colors`}
                            >
                              {inner}
                            </Link>
                          ) : (
                            <div key={i} className={baseCls}>{inner}</div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* ── 1. Ce qu'il faut retenir ── */}
              <section className="mb-12" id="retenir">
                <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-5 flex items-center gap-2">
                  <span className="h-px flex-1 bg-border" />
                  {t('fiche.page.takeaways')}
                  <span className="h-px flex-1 bg-border" />
                </h2>

                {/* Problème de droit — carte sombre */}
                {fiche.probleme && (
                  <div className="rounded-2xl bg-foreground text-background p-6 mb-3 shadow-lg">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest opacity-50">{t('fiche.problem')}</p>
                      {fiche.solution && fiche.solution !== 'other' && (
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${
                          fiche.solution.toLowerCase().includes('cassation') || fiche.solution.toLowerCase().includes('annulation') || fiche.solution.toLowerCase().includes('infirmation')
                            ? 'bg-red-500/20 text-red-300'
                            : fiche.solution.toLowerCase().includes('rejet') || fiche.solution.toLowerCase().includes('confirmation')
                              ? 'bg-emerald-500/20 text-emerald-300'
                              : 'bg-white/10 text-white/70'
                        }`}>
                          {fiche.solution.charAt(0).toUpperCase() + fiche.solution.slice(1)}
                        </span>
                      )}
                    </div>
                    <p className="text-base leading-relaxed font-medium" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                      {fiche.probleme}
                    </p>
                  </div>
                )}

                {/* Solution — carte claire */}
                {fiche.solution_text ? (
                  <div className="rounded-2xl border-2 border-border bg-card p-6 shadow-sm">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
                      {t('fiche.page.court_holding')}
                    </p>
                    {blocked ? (
                      <div className="relative overflow-hidden">
                        <p
                          className="text-sm leading-relaxed text-foreground select-none pointer-events-none"
                          style={{ filter: 'blur(5px)', fontFamily: "'Fraunces', Georgia, serif" }}
                        >
                          {fiche.solution_text.slice(0, 200)}
                        </p>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Link
                            to="/pricing"
                            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold shadow hover:bg-primary/90 transition-colors"
                          >
                            {t('fiche.page.unlock_holding')}
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm leading-relaxed text-foreground" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                        {fiche.solution_text}
                      </p>
                    )}
                  </div>
                ) : !blocked && (
                  <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground text-center">
                    {t('fiche.page.holding_pending')}
                  </div>
                )}
              </section>

              {/* ── 2. Fondements ── */}
              {(fiche.articles.length > 0 || fiche.concepts.length > 0 || fiche.themes.length > 0) && (
                <section className="mb-12" id="fondements">
                  <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-5 flex items-center gap-2">
                    <span className="h-px flex-1 bg-border" />
                    {t('fiche.page.foundations')}
                    <span className="h-px flex-1 bg-border" />
                  </h2>

                  {fiche.articles.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('fiche.tab.articles')}</p>
                      <div className="flex flex-wrap gap-2">
                        {fiche.articles.map((a, i) => {
                          const path = articlePathFromFiche(a);
                          const label = articleLabel(a.code, a.article);
                          const cls = "inline-flex items-center gap-1.5 text-sm font-mono px-3 py-1.5 rounded-full transition-colors";
                          return path ? (
                            <Link
                              key={i}
                              target="_blank"
                              rel="noopener noreferrer"
                              to={path}
                              className={`${cls} bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/40`}
                            >
                              {label}
                              <svg className="w-3 h-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </Link>
                          ) : (
                            <span key={i} className={`${cls} bg-muted text-muted-foreground cursor-default`} title={t('fiche.page.code_not_indexed')}>
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {fiche.concepts.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('fiche.page.concepts')}</p>
                      <div className="flex flex-wrap gap-2">
                        {fiche.concepts.map((c, i) => (
                          <span key={i} className="text-sm bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-3 py-1.5 rounded-full">
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {fiche.themes.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">{t('fiche.tab.themes')}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {fiche.themes.map((theme, i) => (
                          <span key={i} className="text-xs bg-muted text-muted-foreground px-2.5 py-1 rounded-lg">{theme}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* ── 4. Bannière promo minimale (non abonnés) ── */}
              {!user && (
                <div className="mb-12 flex items-center gap-4 rounded-xl border border-primary/20 bg-primary/5 px-5 py-3.5">
                  <span className="text-xl shrink-0">⚖️</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">SmartLawyer Pro</p>
                    <p className="text-xs text-muted-foreground">{t('fiche.page.promo_desc')}</p>
                  </div>
                  <Link
                    to="/pricing"
                    className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    {t('fiche.page.promo_try')}
                  </Link>
                </div>
              )}

              {/* ── 4. Approfondir (onglets) ── */}
              <section className="mb-12" id="approfondir">
                <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-5 flex items-center gap-2">
                  <span className="h-px flex-1 bg-border" />
                  {t('fiche.page.deep_dive')}
                  <span className="h-px flex-1 bg-border" />
                </h2>

                {/* Onglets compacts */}
                <div className="flex gap-1 mb-6 border-b overflow-x-auto">
                  {([
                    ['faits',     t('fiche.page.tab_facts')],
                    ['relations', `${t('fiche.page.tab_relations')}${totalRel ? ` (${totalRel})` : ''}`],
                    ['graphe',    t('fiche.page.tab_graph')],
                    ['source',    t('fiche.page.tab_source')],
                  ] as [DeepTab, string][]).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setDeepTab(key)}
                      className={`px-4 py-2 text-sm font-medium rounded-t-lg -mb-px border transition-colors whitespace-nowrap ${
                        deepTab === key
                          ? 'bg-background border-border border-b-background text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Faits & contexte */}
                {deepTab === 'faits' && (
                  <div className="space-y-6">
                    {fiche.faits && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">{t('fiche.facts')}</p>
                        {blocked ? (
                          <div className="relative overflow-hidden">
                            <p className="text-sm leading-relaxed text-foreground select-none pointer-events-none"
                               style={{ filter: 'blur(5px)' }}>
                              {fiche.faits.slice(0, 300)}…
                            </p>
                            <div className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
                                 style={{ background: 'linear-gradient(to bottom, transparent, hsl(var(--background)))' }} />
                          </div>
                        ) : (
                          <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{fiche.faits}</p>
                        )}
                      </div>
                    )}
                    {fiche.procedure && !blocked && (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3">{t('fiche.procedure')}</p>
                        <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">{fiche.procedure}</p>
                      </div>
                    )}
                    {blocked && <PaywallOverlay userType={userType} />}
                  </div>
                )}

                {/* Relations */}
                {deepTab === 'relations' && (
                  blocked
                    ? <PaywallOverlay userType={userType} />
                    : <FicheTabRelations
                        fiche={fiche}
                        relations={relations}
                        loadingRel={loadingRel}
                        blocked={blocked}
                      />
                )}

                {/* Legal Graph */}
                {deepTab === 'graphe' && (
                  blocked
                    ? <PaywallOverlay userType={userType} />
                    : loadingRel
                      ? (
                        <div className="flex flex-col items-center justify-center py-24 gap-4">
                          <Loader className="!size-8" />
                          <p className="text-sm text-muted-foreground">{t('fiche.rel.loading')}</p>
                        </div>
                      )
                      : !relations
                        ? (
                          <div className="text-center py-24 text-muted-foreground">
                            <p className="text-5xl mb-4">🕸️</p>
                            <p className="font-semibold text-foreground mb-1">{t('fiche.rel.unavailable')}</p>
                            <p className="text-sm">{t('fiche.rel.not_enriched')}</p>
                          </div>
                        )
                        : <LegalGraphTab fiche={fiche} relations={relations} />
                )}

                {/* Texte intégral */}
                {deepTab === 'source' && (
                  blocked
                    ? <PaywallOverlay userType={userType} />
                    : <FicheTabSource
                        fiche={fiche}
                        source={source}
                        loadingSource={loadingSource}
                        sourceError={sourceError}
                      />
                )}
              </section>

              {/* ── 5. CTA dark ── */}
              {!user && (
                <div className="rounded-2xl bg-foreground text-background p-8 mb-12 text-center shadow-xl">
                  <p className="text-xs font-bold uppercase tracking-widest opacity-40 mb-3">SmartLawyer</p>
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                    {t('fiche.page.cta_title')}
                  </h3>
                  <p className="text-sm opacity-70 mb-6 max-w-md mx-auto">
                    {t('fiche.page.cta_desc')}
                  </p>
                  <div className="flex justify-center gap-3 flex-wrap">
                    <Link
                      to="/pricing"
                      className="px-5 py-2.5 rounded-xl bg-background text-foreground font-semibold text-sm hover:bg-background/90 transition-colors"
                    >
                      {t('fiche.page.cta_offers')}
                    </Link>
                    <Link
                      to="/jurisprudence"
                      className="px-5 py-2.5 rounded-xl border border-white/20 text-background/80 font-medium text-sm hover:border-white/40 transition-colors"
                    >
                      {t('fiche.page.cta_explore')}
                    </Link>
                  </div>
                </div>
              )}

            </main>

            {/* ════════════════════════════════════════════════════════════════
                Sidebar desktop (sticky)
            ════════════════════════════════════════════════════════════════ */}
            <aside className="hidden lg:flex flex-col gap-4 shrink-0 w-64 sticky top-6">

              {/* Card Agent IA */}
              <div className="rounded-2xl border bg-card shadow-sm overflow-hidden">
                {/* En-tête carte agent */}
                <div className="px-4 py-3 bg-gradient-to-br from-primary/5 to-primary/10 border-b">
                  <div className="flex items-center gap-2.5 mb-1">
                    {agentImage ? (
                      <img src={agentImage} alt={agentDisplayName}
                           className="w-8 h-8 rounded-full object-contain flex-shrink-0 ring-2 ring-primary/20" />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-base flex-shrink-0">
                        {agentDisplayEmoji}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{agentDisplayName}</p>
                      <p className="text-[10px] text-muted-foreground">{t('fiche.page.agent_specialized')}</p>
                    </div>
                    <span className="ml-auto text-[9px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 px-1.5 py-0.5 rounded-full shrink-0">
                      {t('fiche.page.ai_badge')}
                    </span>
                  </div>
                  {fiche.domaine && (
                    <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
                      {t('fiche.page.expert_in')}{' '}
                      <span className="font-medium text-foreground">{fiche.domaine}</span>
                    </p>
                  )}
                </div>

                {/* Corps : suggestions */}
                <div className="px-4 py-3 space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">
                    {t('fiche.page.suggested_questions')}
                  </p>
                  {agentSuggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => blocked ? undefined : openChat(s)}
                      disabled={blocked}
                      className="w-full text-left text-xs px-3 py-2 rounded-lg border border-border bg-background hover:bg-muted/50 hover:border-primary/30 transition-colors leading-snug disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {s}
                    </button>
                  ))}
                </div>

                {/* Footer carte */}
                <div className="px-4 pb-4">
                  {blocked ? (
                    <Link
                      to="/pricing"
                      className="w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
                    >
                      {t('fiche.page.unlock_agent')}
                    </Link>
                  ) : (
                    <button
                      onClick={() => openChat()}
                      className="w-full flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors border border-primary/20"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      {t('fiche.page.talk_to', { name: agentDisplayName })}
                    </button>
                  )}
                </div>
              </div>

              {/* Timeline procédurale */}
              <ProcedureTimeline
                ficheId={fiche.id}
                fiche={fiche}
                relations={relations}
                relationsLoading={loadingRel}
              />
            </aside>

          </div>
        </div>
      </div>

      <PublicFooter />

      {/* ══════════════════════════════════════════════════════════════════════
          Chat overlay — slide du bas
      ══════════════════════════════════════════════════════════════════════ */}
      {chatOpen && (
        <>
          {/* Backdrop flou */}
          <div
            className="fixed inset-0 z-40 bg-background/60 backdrop-blur-sm"
            onClick={() => setChatOpen(false)}
          />

          {/* Panel chat */}
          <div
            className="fixed inset-x-0 bottom-0 z-50 flex flex-col rounded-t-3xl bg-background border-t border-x border-border shadow-2xl"
            style={{ maxHeight: '85vh', animation: 'slideUp 0.3s ease-out' }}
          >
            {/* Bande contexte fiche */}
            <div className="flex items-center gap-3 px-5 py-3 bg-foreground text-background rounded-t-3xl shrink-0">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-0.5">{t('fiche.page.analyzing')}</p>
                <p className="text-sm font-medium truncate" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                  {fiche.jurisdiction}{fiche.chamber ? ` · ${fiche.chamber}` : ''} — {fiche.decision_date?.slice(0, 10)}
                </p>
                {fiche.probleme && (
                  <p className="text-xs opacity-60 truncate mt-0.5">{fiche.probleme.slice(0, 90)}…</p>
                )}
              </div>
              {hasDesktopBridge && (
                <div className="shrink-0 flex text-[11px] rounded-full bg-white/10 p-0.5">
                  <button onClick={() => setLocalAgent(false)} className={`px-2.5 py-1 rounded-full ${!localAgent ? 'bg-white/25' : 'opacity-60'}`}>
                    {t('desktop.home.local_ai_off')}
                  </button>
                  <button onClick={() => setLocalAgent(true)} className={`px-2.5 py-1 rounded-full ${localAgent ? 'bg-white/25' : 'opacity-60'}`}>
                    {t('desktop.home.local_ai_on')}
                  </button>
                </div>
              )}
              <button
                onClick={() => setChatOpen(false)}
                className="shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label={t('common.close')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Suggestions rapides si pas de prompt initial */}
            {!initialPrompt && (
              <div className="flex gap-2 px-5 py-2.5 border-b overflow-x-auto shrink-0">
                {agentSuggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setInitialPrompt(s)}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-border bg-muted/50 hover:bg-muted hover:border-primary/30 transition-colors whitespace-nowrap"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Widget chat */}
            <div className="flex-1 overflow-hidden">
              {localAgent ? (
                <LocalAgentChat
                  key={`local-${fiche.id}-${initialPrompt ?? 'open'}`}
                  initialPrompt={initialPrompt}
                  context={[
                    [fiche.jurisdiction, fiche.chamber, fiche.decision_date?.slice(0, 10), fiche.number].filter(Boolean).join(' · '),
                    fiche.probleme && `Problème juridique : ${fiche.probleme}`,
                    fiche.faits && `Faits : ${fiche.faits}`,
                    fiche.solution_text && `Solution : ${fiche.solution_text}`,
                  ].filter(Boolean).join('\n\n')}
                />
              ) : (
                <FicheAgentChat
                  ficheId={fiche.id}
                  ficheTitle={ficheShortTitle}
                  initialPrompt={initialPrompt}
                  key={`${fiche.id}-${initialPrompt ?? 'open'}`}
                />
              )}
            </div>
          </div>
        </>
      )}

      {/* Animation CSS */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      <MCPBadge />
    </>
  );
}
